<template>
  <section
    v-if="variants.v1 === variant"
    class="layout-pt-lg layout-pb-lg"
    :style="{ backgroundColor: `var(--c-${vars.backgroundColor})` }"
  >
    <div
      class="container tw-flex tw-flex-col tw-items-center tw-gap-6 tw-text-black lg:tw-flex-row lg:tw-gap-16 xl:tw-gap-32"
      :class="{ 'lg:tw-flex-row-reverse': vars.isReversedBool }"
    >
      <!-- Media Column -->
      <div
        class="tw-relative tw-w-full tw-flex-[1_1_50%]"
        :class="{
          'tw-mr-10': ['bottom-right', 'top-right'].includes(vars.thumbnailCornerText),
          'tw-ml-10': ['bottom-left', 'top-left'].includes(vars.thumbnailCornerText),
          'tw-mt-8 lg:tw-mt-0': ['top-left', 'top-right'].includes(vars.thumbnailCornerText),
        }"
      >
        <div class="tw-relative tw-w-full">
          <!-- Image or video content -->
          <library-image v-if="vars.photoImage && !vars.videoUrl" :src="vars.photoImage" class="tw-h-auto tw-w-full" />
          <library-iframe v-else-if="vars.videoUrl" :src="vars.videoUrl" />

          <!-- Bottom-left decorative border element -->
          <div
            class="tw-absolute -tw-top-2 tw-right-0 tw-h-5/6 tw-w-5/6"
            :style="`border-top: 8px solid var(--${vars.borderColor}); border-right: 8px solid var(--${vars.borderColor});`"
          ></div>
        </div>

        <library-image
          v-if="vars.thumbnailImage"
          :src="vars.thumbnailImage"
          class="tw-absolute tw-h-auto tw-w-1/2 tw-transform"
          :class="{
            '-tw-left-10 tw-bottom-0 -tw-translate-x-1/4 tw-translate-y-1/4 tw-border-r-8 tw-border-t-8':
              vars.thumbnailCornerText === 'bottom-left',
            '-tw-right-12 tw-bottom-0 tw-translate-y-2/4 tw-border-l-8 tw-border-t-8':
              vars.thumbnailCornerText === 'bottom-right',
            '-tw-left-10 -tw-top-1/4 -tw-translate-x-1/4 tw-translate-y-1/4 tw-border-b-8 tw-border-r-8':
              vars.thumbnailCornerText === 'top-left',
            '-tw-right-10 -tw-top-1/4 -tw-translate-x-1/4 tw-translate-y-1/4 tw-border-b-8 tw-border-l-8':
              vars.thumbnailCornerText === 'top-right',
          }"
          :style="{ borderColor: `var(--c-${vars.backgroundColor})` }"
        />
      </div>

      <!-- Content Column -->
      <div class="tw-mt-8 tw-flex tw-flex-[1_1_50%] tw-flex-col tw-gap-6 lg:tw-mt-0 lg:tw-gap-8">
        <!-- Title -->
        <div v-html="vars.titleHtml" class="tw-flex tw-flex-row tw-flex-wrap" />

        <!-- Descriptions -->
        <div v-if="descriptions" class="tw-flex tw-max-w-2xl tw-flex-col tw-gap-4">
          <p
            v-for="(item, idx) in descriptions"
            :key="`desc-${idx}`"
            class="tw-whitespace-normal"
            :style="{ color: `var(--c-${vars.textColor})` }"
          >
            {{ item.description }}
          </p>
        </div>

        <!-- Buttons -->
        <div v-if="buttons" class="tw-mt-6 tw-flex tw-flex-wrap tw-gap-3">
          <nuxt-link
            v-for="(button, idx) in buttons"
            :key="`btn-${idx}`"
            :to="button.link"
            :class="[buttons.length > 1 ? 'tw-w-full md:tw-w-auto' : 'tw-w-auto', `n-${button.type}`]"
            :external="true"
          >
            {{ button.label }}
          </nuxt-link>
        </div>
      </div>
    </div>
  </section>

  <section
    v-else-if="variants.v2 === variant"
    class="layout-pt-lg layout-pb-lg"
    :style="{ backgroundColor: `var(--c-${vars.backgroundColor}, var(--c-section_primary))` }"
  >
    <div
      class="container tw-flex tw-flex-col tw-items-center tw-gap-6 tw-text-black lg:tw-flex-row lg:tw-gap-16 xl:tw-gap-32"
      :class="{ 'lg:tw-flex-row-reverse': vars.isReversedBool }"
    >
      <!-- Media Column -->
      <div class="tw-relative tw-w-full tw-flex-[1_1_50%]">
        <!-- Main image or video -->
        <div class="tw-relative">
          <library-image v-if="vars.photoImage && !vars.videoUrl" :src="vars.photoImage" class="tw-h-auto tw-w-full" />
          <library-iframe v-else-if="vars.videoUrl" :src="vars.videoUrl" />

          <!-- SVG Overlay with section divider lines -->
          <!-- SVG Overlay with section divider lines -->
          <svg
            v-if="!vars.videoUrl"
            class="tw-pointer-events-none tw-absolute tw-left-1 tw-top-0 tw-h-full tw-w-full"
            xmlns="http://www.w3.org/2000/svg "
            preserveAspectRatio="none"
            viewBox="0 0 800 500"
          >
            <!-- Existing lines -->
            <line
              x1="550"
              y1="0"
              x2="550"
              y2="500"
              :style="{ stroke: `var(--c-${vars.backgroundColor})` }"
              stroke-width="6"
            />
            <line
              x1="550"
              y1="250"
              x2="800"
              y2="250"
              :style="{ stroke: `var(--c-${vars.backgroundColor})` }"
              stroke-width="6"
            />

            <!-- New thicker lines for red sections -->
            <!-- Right border of the image -->
            <line
              x1="800"
              y1="0"
              x2="800"
              y2="250"
              :style="{ stroke: `var(--c-${vars.backgroundColor})` }"
              stroke-width="60"
            />

            <!-- Top border of the red sections -->
            <line
              x1="550"
              y1="0"
              x2="800"
              y2="0"
              :style="{ stroke: `var(--c-${vars.backgroundColor})` }"
              stroke-width="40"
            />

            <!-- Bottom border of the red sections -->
            <line
              x1="550"
              y1="500"
              x2="800"
              y2="500"
              :style="{ stroke: `var(--c-${vars.backgroundColor})` }"
              stroke-width="40"
            />
          </svg>
        </div>
      </div>

      <!-- Content Column -->
      <div class="tw-flex tw-flex-[1_1_50%] tw-flex-col tw-gap-6 lg:tw-gap-8">
        <!-- Title -->
        <div v-html="vars.titleHtml" class="tw-flex tw-flex-row tw-flex-wrap" />

        <!-- Descriptions -->
        <div v-if="descriptions" class="tw-flex tw-max-w-2xl tw-flex-col tw-gap-4">
          <p
            v-for="(item, idx) in descriptions"
            :key="`desc-${idx}`"
            class="tw-whitespace-normal"
            :style="{ color: `var(--c-${vars.textColor})` }"
          >
            {{ item.description }}
          </p>
        </div>

        <!-- Buttons -->
        <div v-if="buttons" class="tw-flex tw-flex-wrap tw-gap-3">
          <nuxt-link
            v-for="(button, idx) in buttons"
            :key="`btn-${idx}`"
            :to="button.button_link"
            :class="[buttons.length > 1 ? 'tw-w-full md:tw-w-auto' : 'tw-w-auto', `n-${button.type}`]"
            :external="true"
          >
            {{ button.button_label }}
          </nuxt-link>
        </div>

        <!-- SVG Overlay with section divider lines -->
        <svg
          class="tw-pointer-events-none tw-absolute tw-left-0 tw-top-0 tw-h-full tw-w-full"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          viewBox="0 0 800 500"
        ></svg>
      </div>
    </div>
  </section>

  <section
    v-else-if="variants.v3 === variant"
    class="layout-pt-lg layout-pb-lg"
    :style="{ backgroundColor: `var(--c-${vars.backgroundColor})` }"
  >
    <div
      class="container tw-flex tw-max-w-xl tw-flex-col tw-items-center tw-gap-6 tw-text-black lg:tw-max-w-none lg:tw-flex-row lg:tw-gap-16 xl:tw-gap-32"
      :class="{ 'lg:tw-flex-row-reverse': vars.isReversedBool }"
    >
      <!-- Media Column -->
      <div
        class="tw-relative tw-flex-[1_1_50%]"
        :class="{
          'tw-mr-10': ['bottom-right', 'top-right'].includes(vars.thumbnailCornerText),
          'tw-ml-10': ['bottom-left', 'top-left'].includes(vars.thumbnailCornerText),
          'tw-mt-8 lg:tw-mt-0': ['top-left', 'top-right'].includes(vars.thumbnailCornerText),
        }"
      >
        <!-- Main image or video with gold border styled as a decorative element rather than full border -->
        <div class="tw-relative">
          <!-- Gold border as a decorative element -->

          <!-- Image or video content -->
          <NuxtImg v-if="vars.photoImage && !vars.videoUrl" :src="vars.photoImage" class="tw-h-auto tw-w-full" />
          <library-video-embed
            v-else-if="vars.videoUrl"
            :video-url="vars.videoUrl"
            title="Video Popup"
            :is-popup="vars.isPopupBool"
            :cover-photo="vars.photoImage || null"
          />

          <!-- Bottom-left decorative border element -->
          <div
            class="tw-absolute -tw-top-2 tw-right-0 tw-h-5/6 tw-w-5/6"
            :style="`border-top: 8px solid var(--${vars.borderColor}); border-right: 8px solid var(--${vars.borderColor});`"
          ></div>
        </div>

        <NuxtImg
          v-if="vars.thumbnailImage"
          :src="vars.thumbnailImage"
          class="tw-absolute tw-h-auto tw-w-1/2 tw-transform"
          :class="{
            '-tw-left-10 tw-bottom-0 -tw-translate-x-1/4 tw-translate-y-1/4 tw-border-r-8 tw-border-t-8':
              vars.thumbnailCornerText === 'bottom-left',
            '-tw-right-12 tw-bottom-0 tw-translate-y-2/4 tw-border-l-8 tw-border-t-8':
              vars.thumbnailCornerText === 'bottom-right',
            '-tw-left-10 -tw-top-1/4 -tw-translate-x-1/4 tw-translate-y-1/4 tw-border-b-8 tw-border-r-8':
              vars.thumbnailCornerText === 'top-left',
            '-tw-right-10 -tw-top-1/4 -tw-translate-x-1/4 tw-translate-y-1/4 tw-border-b-8 tw-border-l-8':
              vars.thumbnailCornerText === 'top-right',
          }"
          :style="{ borderColor: `var(--c-${vars.backgroundColor})` }"
        />
      </div>

      <!-- Content Column -->
      <div class="tw-mt-8 tw-flex tw-flex-[1_1_50%] tw-flex-col tw-gap-6 lg:tw-mt-0 lg:tw-gap-8">
        <!-- Title -->
        <h2 class="tw-flex tw-flex-row tw-flex-wrap" :style="{ color: `var(--c-${vars.textColor})` }">
          {{ vars.titleText }}
        </h2>

        <!-- Descriptions -->
        <div v-if="descriptions" class="tw-flex tw-max-w-2xl tw-flex-col tw-gap-4">
          <p
            v-for="(item, idx) in descriptions"
            :key="`desc-${idx}`"
            class="tw-whitespace-normal"
            :style="{ color: `var(--c-${vars.textColor})` }"
          >
            {{ item.description }}
          </p>
        </div>

        <!-- Buttons -->
        <div v-if="buttons" class="tw-mt-6 tw-flex tw-flex-wrap tw-gap-3">
          <nuxt-link
            v-for="(button, idx) in buttons"
            :key="`btn-${idx}`"
            :to="button.link"
            :class="[buttons.length > 1 ? 'tw-w-full md:tw-w-auto' : 'tw-w-auto', `n-${button.type}`]"
          >
            {{ button.label }}
            <span v-if="company?.company_key === 'keystonepmc'" class="btn-arrow">
              <svg width="23" height="9" viewBox="0 0 23 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M22.3536 4.85355C22.5488 4.65829 22.5488 4.34171 22.3536 4.14645L19.1716 0.964466C18.9763 0.769204 18.6597 0.769204 18.4645 0.964466C18.2692 1.15973 18.2692 1.47631 18.4645 1.67157L21.2929 4.5L18.4645 7.32843C18.2692 7.52369 18.2692 7.84027 18.4645 8.03553C18.6597 8.2308 18.9763 8.2308 19.1716 8.03553L22.3536 4.85355ZM0 4.5V5H22V4.5V4H0V4.5Z"
                  fill="var(--c-text_primary)"
                />
              </svg>
            </span>
          </nuxt-link>
        </div>
      </div>
    </div>
  </section>

  <section
    v-else-if="variants.v4 === variant"
    class="layout-pt-lg layout-pb-lg"
    :style="{ backgroundColor: `var(--c-${vars.backgroundColor})` }"
  >
    <div
      class="tw-flex tw-h-full tw-flex-col lg:tw-flex-row"
      :class="{ 'lg:tw-flex-row-reverse': vars.isReversedBool }"
    >
      <!-- Video Column - Sol tarafa yapışık -->
      <div class="tw-relative tw-flex-[1_1_50%]">
        <div class="tw-h-full">
          <NuxtImg
            v-if="vars.photoImage && !vars.videoUrl"
            :src="vars.photoImage"
            class="tw-h-full tw-w-full tw-border tw-border-gray-300 tw-object-cover"
            alt="Background Image"
          />
          <div v-else-if="vars.videoUrl" class="tw-h-full tw-w-full tw-border tw-border-gray-300 tw-bg-black">
            <library-video-embed
              :video-url="vars.videoUrl"
              title="Background Video"
              :cover-photo="vars.photoImage || null"
              class="tw-h-full tw-w-full"
            />
          </div>
        </div>
      </div>

      <!-- Content Column - Sağ taraf -->
      <div class="tw-flex tw-flex-[1_1_50%] tw-flex-col tw-justify-center tw-px-8 tw-py-16 lg:tw-px-16 lg:tw-py-24">
        <!-- Title -->
        <div class="tw-mb-8">
          <h2
            class="tw-text-4xl tw-font-bold tw-tracking-wide tw-text-gray-800 lg:tw-text-5xl xl:tw-text-6xl"
            :style="{ color: `var(--c-${vars.textColor})` }"
          >
            {{ vars.titleText || 'BACKGROUND' }}
          </h2>
        </div>

        <!-- Descriptions -->
        <div v-if="descriptions" class="tw-flex tw-flex-col tw-gap-4">
          <p
            v-for="(item, idx) in descriptions"
            :key="`desc-${idx}`"
            class="tw-text-base tw-leading-relaxed tw-text-gray-600 lg:tw-text-lg"
            :style="{ color: `var(--c-${vars.textColor})` }"
          >
            {{ item.description }}
          </p>
        </div>

        <!-- Default content if no descriptions provided -->
        <div v-else>
          <p
            class="tw-text-base tw-leading-relaxed tw-text-gray-600 lg:tw-text-lg"
            :style="{ color: `var(--c-${vars.textColor})` }"
          >
            Lorem ipsum dolor sit amet consectetur. Luctus posuere praesent ut id. Orci imperdiet eu convallis
            convallis. Nulla lorem vestibulum neque volutpat amet. Nec pulvinar luctus volutpat tristique dui. Urna
            egestas elit nisl tempus dictum. Tellus dictum ut condimentum at morbi. Iaculis cras dictum ut dui varius id
            vitae eget fermentum. Enim justo facilisis ultrices pretium. Vitae vitae sit morbi mattis ultrices tellus
            quam tellus posuere.
          </p>
        </div>

        <!-- Buttons -->
        <div v-if="buttons" class="tw-mt-8 tw-flex tw-flex-wrap tw-gap-4">
          <nuxt-link
            v-for="(button, idx) in buttons"
            :key="`btn-${idx}`"
            :to="button.link"
            :class="[
              'tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-px-6 tw-py-3 tw-font-semibold tw-transition-all tw-duration-300',
              buttons.length > 1 ? 'tw-w-full md:tw-w-auto' : 'tw-w-auto',
              `n-${button.type}`,
            ]"
          >
            {{ button.label }}
            <svg
              v-if="company?.company_key === 'keystonepmc'"
              class="tw-h-5 tw-w-5 tw-transition-transform tw-duration-300 group-hover:tw-translate-x-1"
              fill="currentColor"
              viewBox="0 0 23 9"
            >
              <path
                d="M22.3536 4.85355C22.5488 4.65829 22.5488 4.34171 22.3536 4.14645L19.1716 0.964466C18.9763 0.769204 18.6597 0.769204 18.4645 0.964466C18.2692 1.15973 18.2692 1.47631 18.4645 1.67157L21.2929 4.5L18.4645 7.32843C18.2692 7.52369 18.2692 7.84027 18.4645 8.03553C18.6597 8.2308 18.9763 8.2308 19.1716 8.03553L22.3536 4.85355ZM0 4.5V5H22V4.5V4H0V4.5Z"
              />
            </svg>
          </nuxt-link>
        </div>
      </div>
    </div>
  </section>

  <section
    v-else-if="variants.v5 === variant"
    class="layout-pt-lg layout-pb-lg"
    :style="{ backgroundColor: `var(--c-${vars.backgroundColor})` }"
  >
    <div
      class="container tw-flex tw-flex-col tw-items-center tw-gap-6 tw-text-black lg:tw-max-w-none lg:tw-flex-row lg:tw-gap-16 xl:tw-gap-32"
      :class="{ 'lg:tw-flex-row-reverse': vars.isReversedBool }"
    >
      <!-- Media Column (always first on mobile, then ordered by lg:flex-row-reverse) -->
      <div class="tw-w-full tw-flex-[1_1_50%]">
        <div class="tw-relative">
          <!-- Fotoğraf -->
          <library-image v-if="vars.photoImage" :src="vars.photoImage" class="tw-mb-4 tw-h-auto tw-w-full" />
          <!-- Video -->
          <library-video-embed
            v-if="vars.videoUrl"
            :video-url="vars.videoUrl"
            title="Video Popup"
            :is-popup="vars.isPopupBool"
            :cover-photo="vars.photoImage || null"
            class="tw-h-auto tw-w-full"
          />
        </div>
      </div>

      <!-- Content Column -->
      <div class="tw-flex tw-w-full tw-flex-[1_1_50%] tw-flex-col tw-gap-10">
        <h2 class="tw-flex tw-max-w-xl tw-flex-wrap" :style="{ color: `var(--c-${vars.textColor})` }">
          {{ vars.titleText }}
        </h2>

        <!-- Descriptions -->
        <div v-for="(desc, i) in descriptions" :key="i" class="tw-flex tw-max-w-2xl tw-flex-col tw-gap-2">
          <h4>{{ desc.title }}</h4>
          <p>{{ desc.description }}</p>
        </div>

        <!-- Cards Grid -->
        <div class="tw-grid tw-max-w-2xl tw-grid-cols-1 tw-gap-8 md:tw-grid-cols-2">
          <div v-for="(card, j) in cards" :key="j" class="tw-flex tw-flex-col tw-gap-2">
            <h4>{{ card.title }}</h4>
            <p>{{ card.description }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section
    v-else-if="variants.v6 === variant"
    class="layout-pt-lg layout-pb-lg"
    :style="{ backgroundColor: `var(--c-${vars.backgroundColor})` }"
  >
    <div
      class="container tw-flex tw-max-w-xl tw-flex-col tw-items-center tw-gap-6 tw-text-black lg:tw-max-w-none lg:tw-flex-row lg:tw-gap-16 xl:tw-gap-32"
      :class="{ 'lg:tw-flex-row-reverse': vars.isReversedBool }"
    >
      <!-- Media Column -->
      <div
        class="tw-relative tw-w-full lg:tw-flex-[1_1_50%]"
        :class="{
          'tw-mr-10': !vars.isReversedBool && vars.thumbnailImage,
          'tw-ml-10': vars.isReversedBool && vars.thumbnailImage,
          'tw-mt-8 lg:tw-mt-0': ['top-left', 'top-right'].includes(vars.thumbnailCornerText),
        }"
      >
        <!-- Main image or video with gold border styled as a decorative element rather than full border -->
        <div class="tw-relative">
          <!-- Gold border as a decorative element -->

          <!-- Image or video content -->
          <NuxtImg v-if="vars.photoImage && !vars.videoUrl" :src="vars.photoImage" class="tw-h-auto tw-w-full" />
          <library-iframe v-else-if="vars.videoUrl" :src="vars.videoUrl" />

          <!-- Bottom-left decorative border element -->
          <div
            v-if="vars.borderColor"
            class="tw-absolute -tw-top-2 tw-right-0 tw-h-5/6 tw-w-5/6"
            :style="`border-top: 8px solid var(--${vars.borderColor}); border-right: 8px solid var(--${vars.borderColor});`"
          ></div>
        </div>

        <NuxtImg
          v-if="vars.thumbnailImage"
          :src="vars.thumbnailImage"
          class="tw-absolute tw-h-auto tw-w-1/2 tw-transform"
          :class="{
            '-tw-left-10 tw-bottom-0 -tw-translate-x-1/4 tw-translate-y-1/4 tw-border-r-8 tw-border-t-8':
              vars.isReversedBool,
            '-tw-right-12 tw-bottom-0 tw-translate-y-2/4 tw-border-l-8 tw-border-t-8': !vars.isReversedBool,
            '-tw-left-10 -tw-top-1/4 -tw-translate-x-1/4 tw-translate-y-1/4 tw-border-b-8 tw-border-r-8':
              vars.thumbnailCornerText === 'top-left',
            '-tw-right-10 -tw-top-1/4 -tw-translate-x-1/4 tw-translate-y-1/4 tw-border-b-8 tw-border-l-8':
              vars.thumbnailCornerText === 'top-right',
          }"
          :style="{ borderColor: `var(--c-${vars.backgroundColor})` }"
        />
      </div>

      <!-- Content Column -->
      <div class="tw-mt-8 tw-flex tw-flex-[1_1_50%] tw-flex-col tw-gap-6 lg:tw-mt-0 lg:tw-gap-8">
        <!-- Title -->
        <h2 class="tw-flex tw-flex-row tw-flex-wrap" :style="{ color: `var(--c-${vars.textColor})` }">
          {{ vars.titleText }}
        </h2>

        <!-- Descriptions -->
        <div v-if="descriptions" class="tw-flex tw-max-w-2xl tw-flex-col tw-gap-4">
          <p
            v-for="(item, idx) in descriptions"
            :key="`desc-${idx}`"
            class="tw-whitespace-normal"
            :style="{ color: `var(--c-${vars.textColor})` }"
          >
            {{ item.description }}
          </p>
        </div>

        <!-- Buttons -->
        <div v-if="buttons" class="tw-mt-6 tw-flex tw-flex-wrap tw-gap-3">
          <nuxt-link
            v-for="(button, idx) in buttons"
            :key="`btn-${idx}`"
            :to="button.button_link"
            :class="[buttons.length > 1 ? 'tw-w-full md:tw-w-auto' : 'tw-w-auto', `n-${button.type}`]"
          >
            {{ button.button_label }}
          </nuxt-link>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineNuxtComponent } from '#app';
import { ComponentMixin } from '~/mixins/component.mixin';
import { mapState } from 'pinia';
import { useWebsiteStore } from '~/units/website/store';

export default defineNuxtComponent({
  name: 'InfoBox',
  mixins: [ComponentMixin],

  data() {
    return {
      variants: {
        v1: 'v1',
        v2: 'v2',
        v3: 'v3',
        v4: 'v4',
        v5: 'v5',
        v6: 'v6',
      },
    };
  },

  computed: {
    ...mapState(useWebsiteStore, ['company']),

    descriptions() {
      return this.groupedVariables.descriptions;
    },
    cards() {
      return this.groupedVariables.cards;
    },
    buttons() {
      return this.groupedVariables.buttons;
    },
  },
});
</script>

<style scoped>
::v-deep library-video-embed iframe {
  height: auto !important;
}
::v-deep iframe {
  z-index: 10 !important;
}
</style>
